import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { FileInfo } from '@classes/file/file-info';
import { FileService } from './file.service';
import { UiService } from './ui.service';
import { Router } from '@angular/router';
import html2canvas from 'html2canvas';
import FileSaver from 'file-saver';

import { MonitoringFilters } from '@classes/monitoring/monitoring-filters';
import { MonitoringFilterItem } from '@classes/monitoring/monitoring-filter-item';
import { MonitoringResultGraph } from '@classes/monitoring/monitoring-result-graph';
import { MonitoringResultTab } from '@classes/monitoring/monitoring-result-tab';
import { MonitoringResults } from '@classes/monitoring/monitoring-results';

import { ApiText } from '@classes/api-text';
import { Report } from '@classes/report/report';
import { ReportItem } from '@classes/report/report-item';
import { ChartType } from '@enums/chart-type.enum';
import { ReportType } from '@classes/report/report-type';
import { ReportTypeCode } from '@enums/report-type-code.enum';
import { DateHelpers } from '../dateHelpers';

@Injectable({
    providedIn: 'root'
})
export class MonitoringService {

    printModeChanged = new Subject<boolean>();

    private baseUrl = environment.apiUrl;

    private report: Report;

    constructor (
        private http:    HttpClient
    ,   private fileSrv: FileService
    ,   private uiSrv:   UiService
    ,   private router:  Router

    ) {}

    getFilters (): Observable<MonitoringFilterItem[]> {
        return this.http.get<MonitoringFilterItem[]>(this.baseUrl + 'monitoring/filters');
    }

    getResults (filters: MonitoringFilters): Observable<MonitoringResults> {
        const postBody = {
            filters  : this.getItemsParameter     (filters.items    )
        ,   startDate: DateHelpers.toHttpParameter(filters.startDate)
        ,   endDate  : DateHelpers.toHttpParameter(filters.endDate  )
        };

        return this.http.post<MonitoringResults>(`${this.baseUrl}monitoring/results`, postBody);
    }

    generateXLS (filters: MonitoringFilters): Observable<Blob> {
        const postBody = {
            filters  : this.getItemsParameter     (filters.items    )
        ,   startDate: DateHelpers.toHttpParameter(filters.startDate)
        ,   endDate  : DateHelpers.toHttpParameter(filters.endDate  )
        };

        return this.http.post(`${this.baseUrl}reports/xls/monitoring`,
        postBody, {observe: 'response', responseType: 'blob'})
            .pipe(map(res => this.saveFile(res)));
    }

    uploadImages (results: MonitoringResults) {
        const printPromises: Promise<FileInfo>[] = [];
        for (let tab of results.tabs) {
            const graphs = tab.graphs.filter(graph => !this.IsEmptyGraph(graph));
            for (let graph of graphs) {
                printPromises.push(this.uploadImage(graph.code));
            }
        }

        this.report = this.ToReport(results.tabs);

        Promise.all(printPromises)
        .then((infos: FileInfo[]) => {
            this.report.reportItems.map((item, i) => item.file = infos[i]);
            this.generatePPT().subscribe(() => { this.printModeChanged.next(false) });
        }, (err) => {
            this.printModeChanged.next(false);
            this.uiSrv.showSnackbar(err);
            this.router.navigate(['/error']);
        });
    }

    private generatePPT (): Observable<Blob> {
        return this.http.post(`${environment.apiUrl}reports/ppt`,
            this.report, {observe: 'response', responseType: 'blob'})
        .pipe(map(res => this.saveFile(res)));
    }

    private getItemsParameter (items: MonitoringFilterItem[]): string {
        if (!items) { return ''; }

        return items.filter(i => i.isSelected).map(i => i.code).sort().join(',');
    }

    private saveFile (res: HttpResponse<Blob>): Blob {
        FileSaver.saveAs(res.body, res.headers.get('filename'));
        return res.body;
    }

    private uploadImage (code: string): Promise<FileInfo> {
        return new Promise((resolve, reject) => {
            const query = document.querySelector(`#monitoring-print #chart-${code}`);
            if(!query) { return reject(new Error(`Chart not found`)); }
            html2canvas(query, {logging: false})
            .then((canvas: HTMLCanvasElement) => {
                canvas.toBlob(blob => {
                    this.fileSrv.uploadSimple(blob)
                    .subscribe(fileInfo => {
                        return resolve(fileInfo);
                    });
                });
            }, () => {
                return reject(new Error(`Image creation failed`));
            });
        });
    }

    private ToReport (tabs: MonitoringResultTab[]): Report {
        const title  = this.uiSrv.translate('main.nav.links.monitoring.shortLabel');

        const report       = new Report()      ;
        report.text        = new ApiText(title);
        report.reportItems = []                ;

        let group          = 1                  ;
        let reportTypeCode = ReportTypeCode.asis;

        for (let tab of tabs) {
            const reportType = new ReportType()      ;
            reportType.code  = reportTypeCode        ;
            reportType.text  = new ApiText(tab.label);

            reportTypeCode =
                reportTypeCode === ReportTypeCode.asis
                    ? ReportTypeCode.kpi
                    : ReportTypeCode.asis;

            const graphs = tab.graphs.filter(graph => !this.IsEmptyGraph(graph));
            for (let graph of graphs) {
                const text = new ApiText(graph.label);
                var item = new ReportItem(text, ChartType.doughnut, reportType);
                item.group = ++group;

                report.reportItems.push(item);
            }
        }

        return report;
    }

    private IsEmptyGraph (graph: MonitoringResultGraph) : boolean {
        return !graph.datas || !graph.datas.some(data => data.value != 0);
    }

}
